import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { 
  Modal, 
  Group, 
  Text, 
  Stack, 
  Box, 
  Loader, 
  Paper, 
  Title, 
  Button,
  ThemeIcon,
  Badge,
  Alert,
} from '@mantine/core';
import { useMantineColorScheme } from '@mantine/core';
import { IconShieldLock, IconAlertCircle } from '@tabler/icons-react';
import './styles.css';

export type Environment = 'dev' | 'qa' | 'production' | 'production-india';

interface PinEntryModalProps {
  opened: boolean;
  onClose: () => void;
  environment: Environment;
  onConfirm: (pin: string) => Promise<void>;
  isSwitching: boolean;
}

const getEnvironmentLabel = (env: Environment): string => {
  switch (env) {
    case 'dev':
      return 'Development';
    case 'qa':
      return 'QA';
    case 'production':
      return 'Production';
    case 'production-india':
      return 'Production India';
    default:
      return env;
  }
};

export function PinEntryModal({
  opened,
  onClose,
  environment,
  onConfirm,
  isSwitching,
}: PinEntryModalProps) {
  const { colorScheme } = useMantineColorScheme();
  const [pin, setPin] = useState<string[]>(['', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const getEnvironmentColor = (env: Environment): string => {
    switch (env) {
      case 'dev':
        return 'blue';
      case 'qa':
        return 'yellow';
      case 'production':
        return 'red';
      case 'production-india':
        return 'orange';
      default:
        return 'gray';
    }
  };

  // Reset PIN when modal opens/closes
  useEffect(() => {
    if (opened) {
      setPin(['', '', '', '']);
      setError(null);
      // Add class to body and html to prevent scrolling and hide roadmap button
      document.body.classList.add('pin-modal-open');
      document.documentElement.classList.add('pin-modal-open');
      // Focus first input after a short delay
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    } else {
      // Remove class when modal closes
      document.body.classList.remove('pin-modal-open');
      document.documentElement.classList.remove('pin-modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('pin-modal-open');
      document.documentElement.classList.remove('pin-modal-open');
    };
  }, [opened]);

  const handlePinChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError(null);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Handle Enter key on last input
    if (e.key === 'Enter' && index === 3 && isPinComplete) {
      handleSubmit();
      return;
    }

    // Handle backspace
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    const digits = pastedData.split('').filter((char) => /^\d$/.test(char));
    
    if (digits.length === 4) {
      const newPin = [...digits];
      setPin(newPin);
      setError(null);
      // Focus last input
      inputRefs.current[3]?.focus();
    }
  };

  const handleSubmit = async () => {
    const pinString = pin.join('');
    
    if (pinString.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }

    try {
      await onConfirm(pinString);
    } catch (err: any) {
      // Extract error message from various error formats
      let errorMessage = 'Invalid PIN. Please try again.';
      
      // Handle string errors
      if (typeof err === 'string') {
        errorMessage = err;
      }
      // Handle Error objects
      else if (err instanceof Error) {
        errorMessage = err.message || errorMessage;
      }
      // Handle error objects with nested structures
      else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error?.message) {
        errorMessage = err.error.message;
      } else if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error?.message) {
        errorMessage = err.data.error.message;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      }
      // Handle case where error.data might be a stringified JSON
      else if (typeof err?.data === 'string') {
        try {
          const parsed = JSON.parse(err.data);
          errorMessage = parsed?.message || parsed?.error?.message || errorMessage;
        } catch {
          errorMessage = err.data;
        }
      }
      // Last resort: try to stringify if it's an object
      else if (typeof err === 'object' && err !== null) {
        // If we can't extract a message, use a default
        console.error('Unhandled error format:', err);
        errorMessage = 'Invalid PIN. Please try again.';
      }
      
      setError(errorMessage);
      // Clear PIN on error
      setPin(['', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };

  const isPinComplete = pin.every((digit) => digit !== '');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={null}
      size="lg"
      centered
      closeOnClickOutside={!isSwitching}
      closeOnEscape={!isSwitching}
      withCloseButton={!isSwitching}
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 4,
      }}
      styles={{
        content: {
          borderRadius: '16px',
        },
        body: {
          padding: 0,
        },
      }}
    >
      <Paper p="xl" radius="md">
        <Stack gap="xl" align="center">
          {/* Header Section */}
          <Stack gap="md" align="center">
            <ThemeIcon
              size={80}
              radius="xl"
              variant="light"
              color={getEnvironmentColor(environment)}
            >
              <IconShieldLock size={40} />
            </ThemeIcon>
            
            <Stack gap="xs" align="center">
              <Title order={2} ta="center" fw={600}>
                Switch Environment
              </Title>
              <Badge
                size="lg"
                color={getEnvironmentColor(environment)}
                variant="light"
                radius="md"
                px="md"
                py="xs"
              >
                {getEnvironmentLabel(environment)}
              </Badge>
              <Text size="sm" c="dimmed" ta="center" mt="xs">
                Enter your PIN to access this environment
              </Text>
            </Stack>
          </Stack>

          {/* PIN Input Section */}
          <Stack gap="md" align="center" w="100%">
            <Group gap="md" justify="center" wrap="nowrap">
              {pin.map((digit, index) => (
                <Box
                  key={index}
                  component="input"
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  disabled={isSwitching}
                  autoComplete="off"
                  className={`pin-input-modern ${digit ? 'pin-input-filled' : ''} ${error ? 'pin-input-error' : ''}`}
                  data-theme={colorScheme}
                />
              ))}
            </Group>

            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Invalid PIN"
                color="red"
                variant="light"
                w="100%"
                styles={{
                  root: {
                    animation: 'shake 0.5s ease',
                  },
                }}
              >
                {error}
              </Alert>
            )}

            {isSwitching && (
              <Group gap="sm" justify="center" mt="md">
                <Loader size="sm" color="violet" />
                <Text size="sm" c="dimmed">
                  Switching environment...
                </Text>
              </Group>
            )}
          </Stack>

          {/* Action Button */}
          {isPinComplete && !isSwitching && (
            <Button
              onClick={handleSubmit}
              size="md"
              fullWidth
              color="violet"
              radius="md"
              styles={{
                root: {
                  marginTop: '1rem',
                  height: '44px',
                  fontSize: '15px',
                  fontWeight: 600,
                },
              }}
            >
              Switch to {getEnvironmentLabel(environment)}
            </Button>
          )}

          {/* Placeholder for button spacing */}
          {!isPinComplete && !isSwitching && !error && (
            <Box h={44} />
          )}
        </Stack>
      </Paper>
    </Modal>
  );
}

